// ============================================================================
// RAID ITEMS API CLIENT
//
// Every mutating call returns the full updated item, so callers patch their
// cache rather than refetching. That matters here: the requirements payload is
// one large blob fed by polling extraction jobs, and invalidating it on every
// resolve would refetch the lot.
// ============================================================================

import { api } from './api';
import { RaidItem, RaidItemsResponse, RaidLink, RaidStatus, RaidType } from '../types/raid';

export interface RaidFilters {
  type?: RaidType;
  status?: RaidStatus;
  owner?: string;
  linkedToId?: string;
  sessionId?: string;
}

export interface RaidItemInput {
  type: RaidType;
  title: string;
  description?: string;
  status?: RaidStatus;
  owner?: string;
  ownerOrg?: string;
  dueDate?: string;
  notes?: string[];
  source?: string;
  tags?: string[];
  links?: RaidLink[];
  dependencyType?: string;
  blockingSeverity?: string;
  targetDate?: string;
  probability?: number;
  impact?: number;
  mitigation?: string;
}

export async function listSessionRaidItems(sessionId: string, filters?: RaidFilters): Promise<RaidItemsResponse> {
  const res = await api.get(`/api/sessions/${sessionId}/raid-items`, { params: filters });
  return res.data;
}

export async function listProjectRaidItems(projectId: string, filters?: RaidFilters): Promise<RaidItemsResponse> {
  const res = await api.get(`/api/projects/${projectId}/raid-items`, { params: filters });
  return res.data;
}

export async function createSessionRaidItem(sessionId: string, input: RaidItemInput): Promise<RaidItem> {
  const res = await api.post(`/api/sessions/${sessionId}/raid-items`, input);
  return res.data.raidItem;
}

export async function createProjectRaidItem(projectId: string, input: RaidItemInput): Promise<RaidItem> {
  const res = await api.post(`/api/projects/${projectId}/raid-items`, input);
  return res.data.raidItem;
}

export async function updateRaidItem(
  scope: { sessionId?: string; projectId?: string },
  raidId: string,
  patch: Partial<RaidItemInput>
): Promise<RaidItem> {
  const res = await api.patch(`${scopePath(scope)}/raid-items/${raidId}`, patch);
  return res.data.raidItem;
}

export async function resolveRaidItem(
  scope: { sessionId?: string; projectId?: string },
  raidId: string,
  body: { status: RaidStatus; resolution?: string }
): Promise<RaidItem> {
  // resolvedBy is taken from the token server-side, never sent from here.
  const res = await api.post(`${scopePath(scope)}/raid-items/${raidId}/resolve`, body);
  return res.data.raidItem;
}

export async function linkRaidItem(
  scope: { sessionId?: string; projectId?: string },
  raidId: string,
  link: RaidLink
): Promise<RaidItem> {
  const res = await api.post(`${scopePath(scope)}/raid-items/${raidId}/links`, link);
  return res.data.raidItem;
}

export async function unlinkRaidItem(
  scope: { sessionId?: string; projectId?: string },
  raidId: string,
  link: Pick<RaidLink, 'targetType' | 'targetId' | 'targetSessionId'>
): Promise<RaidItem> {
  const res = await api.delete(`${scopePath(scope)}/raid-items/${raidId}/links`, { data: link });
  return res.data.raidItem;
}

export async function deleteRaidItem(
  scope: { sessionId?: string; projectId?: string },
  raidId: string
): Promise<void> {
  await api.delete(`${scopePath(scope)}/raid-items/${raidId}`);
}

function scopePath(scope: { sessionId?: string; projectId?: string }): string {
  if (scope.sessionId) return `/api/sessions/${scope.sessionId}`;
  if (scope.projectId) return `/api/projects/${scope.projectId}`;
  throw new Error('A RAID scope needs either a sessionId or a projectId');
}
